import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, PlanType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonRequestTrialLinkComponent from 'webapp/components/common/request-trial-link';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';

export interface Args {
	onClose: () => void;
}

export default class AppNotificationsFreeNotificationComponent<
	T extends Args,
> extends Component<T> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;

	@tracked isRequestTrialInProgress = false;

	trackingOrigin: TrackingOriginTypeForTrialPopupOpened = 'top_banner';

	get usersCountText(): string {
		let text;
		const max = this.billingService.getFeatureValue(BillingFeature.USERS),
			current = this.billingService.getFeatureCurrent(BillingFeature.USERS);
		if (max === null) {
			text = '∞';
		} else {
			text = `${Math.max(0, +max - current)}/${max}`;
		}
		return text;
	}

	get sourcesCountText(): string {
		let text;
		const max = this.billingService.getFeatureValue(BillingFeature.SOURCES),
			current = this.billingService.getFeatureCurrent(BillingFeature.SOURCES);
		if (max === null) {
			text = '∞';
		} else {
			text = `${Math.max(0, +max - current)}/${max}`;
		}
		return text;
	}

	get upgradePrice() {
		const plan = this.billingService.availablePlans
			.filter((item) => item.planType === PlanType.STARTER)
			.find((item) => item.isMonthly);
		return plan?.priceFormatted;
	}

	get showUpgrade(): boolean {
		return !!this.upgradePrice && this.currentUserService.isAdmin;
	}

	get showTrialLink(): boolean {
		return this.billingService.canRequestTrial; // Don't show for this.flowPromotionsService.promotions.hasAnyHigherPlanAutomatedTrialOptionPromotions
	}

	get canBookACall(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	@action
	upgrade() {
		this.navigationService.transitionToRoute('settings.billing');
	}

	@action
	invite() {
		this.mmUsersService.checkToOpenInvitePopup();
	}

	@action
	contactForTrial() {
		if (!this.isRequestTrialInProgress) {
			this.isRequestTrialInProgress = true;
			void this.billingService.requestTrial(null, null, this.trackingOrigin).finally(() => {
				this.isRequestTrialInProgress = false;
			});
		}
	}

	@action
	contactForFreeSetupHelp() {
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, {
			origin: `${this.trackingOrigin}_free_setup_help`,
		});
		window.open(this.accountService.bookACallLink, '_blank');
	}

	@action
	connect() {
		this.navigationService.transitionToRoute('datamanager.available');
	}

	@action
	close() {
		const { onClose } = this.args;

		if (onClose) {
			onClose();
		}
	}
	<template>
		<div class="notification-container free-notification">
			<div class="container">
				<a role="button" class="close" {{on "click" this.close}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>

				<p>You are using a <strong>free version</strong> of Databox.</p>

				<p class="has-icon">
					<CommonUiIconComponent @name="User" @size="sm" />
					<strong>{{this.usersCountText}}</strong>
					users available
					<a role="button" {{on "click" this.invite}}>invite</a>
				</p>

				<p class="has-icon">
					<CommonUiIconComponent @name="Data" @size="sm" />
					<strong>{{this.sourcesCountText}}</strong>
					data sources available
					<a role="button" {{on "click" this.connect}}>connect</a>
				</p>

				{{#if this.showTrialLink}}
					<CommonRequestTrialLinkComponent @trackingOrigin={{this.trackingOrigin}} />
				{{else if this.showUpgrade}}
					<p class="upgrade">
						<a role="button" {{on "click" this.upgrade}}>
							<CommonUiIconComponent @name="ArrowUpCircleFill" @size="sm" />
							Upgrade
							<span class="extra">starting at {{this.upgradePrice}}/mo</span>
						</a>
					</p>
				{{/if}}
				{{#if this.canBookACall}}
					<p class="upgrade">
						<a role="button" {{on "click" this.contactForFreeSetupHelp}}>
							Get free setup help
						</a>
					</p>
				{{/if}}
			</div>
		</div>
	</template>
}
